import React from 'react';
import { Button, Col, DatePicker, Form, Input, Row, Select, Checkbox, Typography, Popconfirm } from 'antd';
import { openNotification, notifyFormValidationFailed, handlePhonePaste, toTurkishUpper } from '../../../utils';
import dayjs from 'dayjs';
import { APP_LOCAL_DATE_FORMAT } from 'config/constants';
import { apiFetch } from 'services/apiClient';
import { createInspector, updateInspector, getInspector } from '../services/inspectorService';
import { CreateInspectorDto } from '../types/Inspector';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export default function InspectorForm({ id, onSuccess, referenceId }: { id?: number; onSuccess: () => void; referenceId?: number }) {
  const [form] = Form.useForm<CreateInspectorDto>();
  const [lat, setLat] = React.useState<number | null>(null);
  const [lng, setLng] = React.useState<number | null>(null);
  const DEFAULT_LAT = 41.0082;
  const DEFAULT_LNG = 28.9784;
  const defaultIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
  });
  // Ehliyet sınıflarını A'dan Z'ye sıralı sunmak için opsiyonları tanımlayıp sıralıyoruz
  const licenseOptions = React.useMemo(
    () =>
      [
        { value: 'A', label: 'A Sınıfı' },
        { value: 'A1', label: 'A1 Sınıfı' },
        { value: 'A2', label: 'A2 Sınıfı' },
        { value: 'B', label: 'B Sınıfı' },
        { value: 'B1', label: 'B1 Sınıfı' },
        { value: 'BE', label: 'BE Sınıfı' },
        { value: 'C', label: 'C Sınıfı' },
        { value: 'C1', label: 'C1 Sınıfı' },
        { value: 'C1E', label: 'C1E Sınıfı' },
        { value: 'CE', label: 'CE Sınıfı' },
        { value: 'D', label: 'D Sınıfı' },
        { value: 'D1', label: 'D1 Sınıfı' },
        { value: 'D1E', label: 'D1E Sınıfı' },
        { value: 'DE', label: 'DE Sınıfı' },
        { value: 'F', label: 'F Sınıfı' },
        { value: 'G', label: 'G Sınıfı' },
        { value: 'M', label: 'M Sınıfı' },
      ].sort((a, b) => a.label.localeCompare(b.label, 'tr')),
    []
  );

  const [stateOptions, setStateOptions] = React.useState<any[]>([]);
  const [districtOptions, setDistrictOptions] = React.useState<any[]>([]);
  const state = Form.useWatch('state', form);

  // Parse "https://maps.google.com/?q=41.070759,28.808908" style links or raw "lat,lng"
  const parseAndApplyLocation = (raw: string) => {
    if (!raw) return;
    let text = raw.trim();
    try {
      const url = new URL(text);
      const qEntry = Array.from(url.searchParams.entries()).find(([key]) => key.toLowerCase() === 'q');
      const q = qEntry ? qEntry[1] : null;
      if (q) text = q;
    } catch {
      // not a URL, may already be lat,lng
    }
    text = text.replace(/%2C/gi, ',');
    const m = text.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
    if (m) {
      const latNum = Number(m[1]);
      const lngNum = Number(m[2]);
      if (!Number.isNaN(latNum) && !Number.isNaN(lngNum)) {
        setLat(latNum);
        setLng(lngNum);
        form.setFieldsValue({ latitude: latNum, longitude: lngNum });
      }
    }
  };
  React.useEffect(() => {
    if (id) {
      getInspector(id).then(d => {
        const data: any = { ...d };
        if (data.birthDate) data.birthDate = dayjs(data.birthDate);
        if (data.hireDate) data.hireDate = dayjs(data.hireDate);
        if (data.terminationDate) data.terminationDate = dayjs(data.terminationDate);
        if (data.healthCheckValidUntil) data.healthCheckValidUntil = dayjs(data.healthCheckValidUntil);
        if (data.occupationalSafetyTrainingValidUntil) data.occupationalSafetyTrainingValidUntil = dayjs(data.occupationalSafetyTrainingValidUntil);
        // Map account basics to top-level editable fields
        if (data.account) {
          data.firstName = data.account.firstName;
          data.lastName = data.account.lastName;
          data.email = data.account.email;
          // phone may come as account.phone or account.phoneNumber
          const phoneRaw: any = (data.account as any).phone ?? (data.account as any).phoneNumber;
          if (typeof phoneRaw === 'string') {
            const digits = phoneRaw.replace(/\D/g, '');
            // Use last 10 digits (Turkish local format) if longer
            data.phoneNumber = digits.length >= 10 ? digits.slice(-10) : digits;
          } else {
            data.phoneNumber = (data.account as any).phoneNumber;
          }
        }
        if (data.state) data.state = typeof data.state === 'object' ? data.state.id : data.state;
        if (data.district) data.district = typeof data.district === 'object' ? data.district.id : data.district;
        // Map driving license to array for the form
        if (data.drivingLicenseClasses && !Array.isArray(data.drivingLicenseClasses)) {
          data.drivingLicenseClasses = [data.drivingLicenseClasses];
        }
        if (!data.drivingLicenseClasses && data.drivingLicenseClass) {
          data.drivingLicenseClasses = [data.drivingLicenseClass];
        }
        if (Array.isArray(data.drivingLicenseClasses)) {
          data.drivingLicenseClasses = data.drivingLicenseClasses.filter((v: any) => v && v !== 'NONE');
        }
        data.noDrivingLicense = !Array.isArray(data.drivingLicenseClasses) || data.drivingLicenseClasses.length === 0;
        if (data.latitude != null) setLat(Number(data.latitude));
        if (data.longitude != null) setLng(Number(data.longitude));
        form.setFieldsValue(data);
      });
    } else {
      form.resetFields();
      setLat(null);
      setLng(null);
    }
    apiFetch('/countries/225/states').then(setStateOptions);
  }, [id, form]);
  React.useEffect(() => { if (state) { apiFetch(`/states/${state}/districts`).then(setDistrictOptions); } else { setDistrictOptions([]); } }, [state]);
  const onFinish = async (values: CreateInspectorDto) => {
    const data: any = { ...values };
    data.firstName = toTurkishUpper(data.firstName)?.trim() ?? data.firstName;
    data.lastName = toTurkishUpper(data.lastName)?.trim() ?? data.lastName;
    data.address = toTurkishUpper(data.address)?.trim() ?? data.address;
    if (data.birthDate) data.birthDate = data.birthDate.format('YYYY-MM-DD');
    if (data.hireDate) data.hireDate = data.hireDate.format('YYYY-MM-DD');
    if (data.healthCheckValidUntil) data.healthCheckValidUntil = data.healthCheckValidUntil.format('YYYY-MM-DD');
    if (data.terminationDate) data.terminationDate = data.terminationDate.format('YYYY-MM-DD');
    if (lat != null && (data.latitude == null || data.latitude === '')) data.latitude = lat;
    if (lng != null && (data.longitude == null || data.longitude === '')) data.longitude = lng;
    if (data.occupationalSafetyTrainingValidUntil) data.occupationalSafetyTrainingValidUntil = data.occupationalSafetyTrainingValidUntil.format('YYYY-MM-DD');
    // For new API: account fields are top-level; remove legacy mapping
    if (id) {
      // On edit, do not send password fields if empty
      if (!data.password) delete data.password;
      if (!data.passwordRepeat) delete data.passwordRepeat;
    }
    // Driving license mapping
    if (data.noDrivingLicense) {
      data.drivingLicenseClasses = null;
      delete data.noDrivingLicense;
    } else {
      if (data.drivingLicenseClasses && !Array.isArray(data.drivingLicenseClasses)) {
        data.drivingLicenseClasses = [data.drivingLicenseClasses];
      }
      if (Array.isArray(data.drivingLicenseClasses)) {
        data.drivingLicenseClasses = data.drivingLicenseClasses.filter((v: any) => v && v !== 'NONE');
        if (data.drivingLicenseClasses.length === 0) data.drivingLicenseClasses = null;
      }
    }
    try {
      if (id) {
        await updateInspector(id, data);
        openNotification('success', 'Başarılı', 'P.K.M Uzmanı Güncellendi');
      } else {
        await createInspector(data);
        openNotification('success', 'Başarılı', 'P.K.M Uzmanı Eklendi');
      }
      onSuccess();
    } catch (e: any) {
      let message = 'P.K.M Uzmanı işlemi başarısız';
      if (e?.message) {
        try {
          const payload = JSON.parse(e.message);
          if (payload?.message) message = payload.message;
        } catch {
          message = e.message;
        }
      }
      openNotification('error', 'Hata', message);
    }
  };
  return (
    <>

      <Form
        form={form}
        onFinish={onFinish}
        onFinishFailed={(info) =>
          notifyFormValidationFailed(info, 'Devam edebilmeniz için zorunlu alanların doldurulması gerekmektedir.', false)
        }
        scrollToFirstError
        layout='vertical'
        style={{ marginBottom: 100 }}
      >
        <Row gutter={[16, 16]}>
          {!id && (
            <>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name='firstName' label='Ad' rules={[{ required: true, message: 'Lütfen isim giriniz !' }]}>
                  <Input lang='tr' style={{ textTransform: 'uppercase' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name='lastName' label='Soyad' rules={[{ required: true, message: 'Lütfen soyisim giriniz !' }]}>
                  <Input lang='tr' style={{ textTransform: 'uppercase' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name='email' label='E-posta' rules={[{ required: true, type: 'email' }]}>
                  <Input  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name='phoneNumber'
                  label='Telefon'
                  rules={[
                    { required: true, message: 'Lütfen telefon numarasını giriniz !' },
                    { pattern: /^\d{10}$/, message: 'Telefon numarası 10 haneli olmalı' },
                  ]}
                >
                  <Input
                    addonBefore="+90"
                    maxLength={10}
                    onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                    onPaste={(e) => handlePhonePaste(e, { form, fieldName: 'phoneNumber' })}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name='notificationPhoneNumber'
                  label='PKM Bilgilendirme Telefon Numarası'
                  rules={[
                    { pattern: /^\d{10}$/, message: 'Telefon numarası 10 haneli olmalı' },
                  ]}
                >
                  <Input
                    addonBefore="+90"
                    maxLength={10}
                    onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                    onPaste={(e) => handlePhonePaste(e, { form, fieldName: 'notificationPhoneNumber' })}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name='password' label='Şifre' rules={[{ required: true, min: 8 }]}>
                  <Input.Password />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name='passwordRepeat'
                  label='Şifre (Tekrar)'
                  dependencies={['password']}
                  rules={[
                    { required: true, min: 8 },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) return Promise.resolve();
                        return Promise.reject(new Error('Şifreler eşleşmiyor'));
                      },
                    }),
                  ]}
                >
                  <Input.Password />
                </Form.Item>
              </Col>
            </>
          )}
          {id && (
            <>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name='firstName' label='Ad' rules={[{ required: true, message: 'Lütfen isim giriniz !' }]}>
                  <Input lang='tr' style={{ textTransform: 'uppercase' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name='lastName' label='Soyad' rules={[{ required: true, message: 'Lütfen soyisim giriniz !' }]}>
                  <Input lang='tr' style={{ textTransform: 'uppercase' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name='phoneNumber'
                  label='Telefon'
                  rules={[
                    { required: true, message: 'Lütfen telefon numarasını giriniz !' },
                    { pattern: /^\d{10}$/, message: 'Telefon numarası 10 haneli olmalı' },
                  ]}
                >
                  <Input
                    addonBefore="+90"
                    maxLength={10}
                    onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                    onPaste={(e) => handlePhonePaste(e, { form, fieldName: 'phoneNumber' })}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name='notificationPhoneNumber'
                  label='PKM Bilgilendirme Telefon Numarası'
                  rules={[
                    { pattern: /^\d{10}$/, message: 'Telefon numarası 10 haneli olmalı' },
                  ]}
                >
                  <Input
                    addonBefore="+90"
                    maxLength={10}
                    onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                    onPaste={(e) => handlePhonePaste(e, { form, fieldName: 'notificationPhoneNumber' })}
                  />
                </Form.Item>
              </Col>
            </>
          )}
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name='identityNumber'
              label='Kimlik Numarası'
              rules={[
                { required: true, message: 'Lütfen TC Kimlik No giriniz !' },
                { pattern: /^[0-9]*$/, message: 'TC Kimlik No sadece rakamlardan oluşmalıdır' },
                {
                  validator: (_, _value) => {
                    const value = _value as string;
                    if (!value) return Promise.resolve();
                    if (value.length !== 11) return Promise.reject('TC Kimlik Numarası 11 haneli olmalıdır');
                    const firstTenDigits = value.substring(0, 10).split('').map(Number);
                    const sumOfFirstTen = firstTenDigits.reduce((acc, curr) => acc + curr, 0);
                    const lastDigitOfSum = sumOfFirstTen % 10;
                    if (lastDigitOfSum !== Number(value[10])) return Promise.reject('TC Kimlik Numarası geçersiz.');
                    return Promise.resolve();
                  }
                },
              ]}
            >
              <Input
                maxLength={11}
                onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item name='birthDate' label='Doğum Tarihi' rules={[{ required: true, message: 'Lütfen doğum tarihi bilgisini giriniz !' }]}>
              <DatePicker
                format={APP_LOCAL_DATE_FORMAT}
                placeholder='Seçiniz'
                style={{ width: '100%' }}
                disabledDate={(d) => !!d && d.isAfter(dayjs().endOf('day'))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.noDrivingLicense !== cur.noDrivingLicense}>
              {() => {
                const noDL = form.getFieldValue('noDrivingLicense');
                return (
                  <Form.Item
                    name='drivingLicenseClasses'
                    label='Ehliyet Sınıfları'
                    rules={noDL ? [] : [{ required: true, message: 'Ehliyet sınıfı zorunludur' }]}
                  >
                    <Select
                      mode='multiple'
                      showSearch
                      style={{minHeight: 48, height: 'auto'}}
                      disabled={!!noDL}
                      filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))}
                      placeholder='Seçiniz'
                    >
                      {licenseOptions.map((opt) => (
                        <Select.Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              }}
            </Form.Item>
            <Form.Item name='noDrivingLicense' valuePropName='checked' style={{ marginTop: -8 }}>
              <Checkbox onChange={(e) => { if (e.target.checked) form.setFieldsValue({ drivingLicenseClasses: [] }); }}>Ehliyeti yok</Checkbox>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Form.Item name='state' label='İl' rules={[{ required: true }]}>
              <Select showSearch filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))} placeholder='Seçiniz'>
                {stateOptions.map(o => <Select.Option key={o.id} value={o.id}>{o.name ?? o.id}</Select.Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Form.Item name='district' label='İlçe' rules={[{ required: true }]}>
              <Select showSearch filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))} placeholder='Seçiniz'>
                {districtOptions.map(o => <Select.Option key={o.id} value={o.id}>{o.name ?? o.id}</Select.Option>)}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name='address' label='Adres' rules={[{ required: true, message: 'Lütfen adres bilgisini giriniz !' }]}>
              <Input.TextArea rows={3} lang='tr' style={{ textTransform: 'uppercase' }} />
            </Form.Item>
          </Col>

          {/* Latitude/Longitude inputs hidden; coordinates are set via map or link */}


          <Col xs={24} sm={12} md={12}>
            <Form.Item name='hireDate' label='İşe Giriş Tarihi'>
              <DatePicker format={APP_LOCAL_DATE_FORMAT} placeholder='Seçiniz' style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Form.Item
              name='terminationDate'
              label='İşten Çıkış Tarihi'
              dependencies={['hireDate']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const hire = getFieldValue('hireDate');
                    if (!value || !hire) return Promise.resolve();
                    if (dayjs(value).isBefore(dayjs(hire), 'day')) {
                      return Promise.reject(new Error('İşten çıkış tarihi, işe giriş tarihinden önce olamaz'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker format={APP_LOCAL_DATE_FORMAT} placeholder='Seçiniz' style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Form.Item name='healthCheckValidUntil' label='Sağlık Muayenesi Geçerlilik Tarihi'>
              <DatePicker format={APP_LOCAL_DATE_FORMAT} placeholder='Seçiniz' style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Form.Item name='occupationalSafetyTrainingValidUntil' label='İş Güvenliği Temel Eğitim Geçerlilik Tarihi'>
              <DatePicker format={APP_LOCAL_DATE_FORMAT} placeholder='Seçiniz' style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name='locationLink' label='Konum Linki (Google Maps)'>
              <Input
                placeholder="https://maps.google.com/?q=41.070759,28.808908 veya 41.070759,28.808908"
                onChange={(e) => parseAndApplyLocation(e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Typography.Text>Konum (Harita üzerinden seçiniz)</Typography.Text>
            <div style={{ height: 320, borderRadius: 8, overflow: 'hidden', marginTop: 8, position: 'relative' }}>
              {/* Sil (koordinatları temizle) butonu */}
              {(lat !== null && lng !== null) && (
                <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 1000 }}>
                  <Popconfirm
                    title="Emin misiniz?"
                    okText="Evet"
                    cancelText="Hayır"
                    onConfirm={() => {
                      setLat(null);
                      setLng(null);
                      form.setFieldsValue({ latitude: null, longitude: null });
                    }}
                  >
                    <Button danger size='small'>Seçimi kaldır</Button>
                  </Popconfirm>
                </div>
              )}
              <MapContainer
                center={lat != null && lng != null ? [lat, lng] as any : [DEFAULT_LAT, DEFAULT_LNG]}
                zoom={lat != null && lng != null ? 12 : 8}
                style={{ height: '100%', width: '100%' }}
              >
                {/* Ensure proper sizing when Drawer opens (mounted) */}
                <InvalidateSizeOnMount />
                {/* Recenter map when coordinates change */}
                <FlyToPosition lat={lat} lng={lng} />
                <TileLayer attribution='&copy; OpenStreetMap contributors' url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' />
                {lat != null && lng != null && <Marker position={[lat, lng] as any} icon={defaultIcon} />}
                <MapClick onClick={(e: { lat: number; lng: number }) => { setLat(e.lat); setLng(e.lng); form.setFieldsValue({ latitude: e.lat, longitude: e.lng }); }} />
              </MapContainer>
            </div>
            {lat != null && lng != null && (
              <div style={{ marginTop: 8 }}>
                <a href={`https://maps.google.com/?q=${lat},${lng}`} target="_blank" rel="noreferrer">Google Maps'te aç</a>
              </div>
            )}
          </Col>
        </Row>
        <Row justify={"end"}>
          <Button htmlType='submit' type='primary' style={{ marginTop: 16, width: 130 }}>Kaydet</Button>
        </Row>
      </Form>
    </>
  );
}

function MapClick({ onClick }: { onClick: (e: { lat: number; lng: number }) => void }) {
  useMapEvents({
    click(e: any) {
      onClick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

// Koordinatlar değiştiğinde haritayı yeni konuma taşı
function FlyToPosition({ lat, lng }: { lat: number | null; lng: number | null }) {
  const map = useMap();
  React.useEffect(() => {
    if (lat != null && lng != null) {
      try {
        map.setView([lat, lng] as any, Math.max(map.getZoom(), 12));
      } catch { }
    }
  }, [lat, lng, map]);
  return null;
}

// Drawer açıldığında mount ile birlikte Leaflet boyutunu güncelle
function InvalidateSizeOnMount() {
  const map = useMap();
  React.useEffect(() => {
    const invalidate = () => { try { map.invalidateSize(); } catch { } };
    // Birkaç tekrar ile animasyon/pencere boyutu gecikmelerini yakala
    invalidate();
    const t1 = setTimeout(invalidate, 100);
    const t2 = setTimeout(invalidate, 300);
    const t3 = setTimeout(invalidate, 600);
    return () => {
      clearTimeout(t1 as any);
      clearTimeout(t2 as any);
      clearTimeout(t3 as any);
    };
  }, [map]);
  return null;
}
